import React from 'react';

// FDI (European) Numbering System
// Upper row (viewer's left to right): patient's right Q1 then patient's left Q2
const UPPER_TEETH = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
// Lower row (viewer's left to right): patient's right Q4 then patient's left Q3
const LOWER_TEETH = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

const TOOTH_NAMES = {
  18: '3rd Molar (Wisdom)', 17: '2nd Molar', 16: '1st Molar',
  15: '2nd Premolar', 14: '1st Premolar', 13: 'Canine',
  12: 'Lateral Incisor', 11: 'Central Incisor',
  21: 'Central Incisor', 22: 'Lateral Incisor', 23: 'Canine',
  24: '1st Premolar', 25: '2nd Premolar', 26: '1st Molar',
  27: '2nd Molar', 28: '3rd Molar (Wisdom)',
  38: '3rd Molar (Wisdom)', 37: '2nd Molar', 36: '1st Molar',
  35: '2nd Premolar', 34: '1st Premolar', 33: 'Canine',
  32: 'Lateral Incisor', 31: 'Central Incisor',
  41: 'Central Incisor', 42: 'Lateral Incisor', 43: 'Canine',
  44: '1st Premolar', 45: '2nd Premolar', 46: '1st Molar',
  47: '2nd Molar', 48: '3rd Molar (Wisdom)'
};

// Determine tooth type by FDI number
const getToothType = (num) => {
  const unit = num % 10;
  if (unit >= 6) return 'molar';
  if (unit >= 4) return 'premolar';
  if (unit === 3) return 'canine';
  return 'incisor';
};

const ToothShape = ({ num, x, y, isUpper, isSelected, isTreated, isMultiTreated, onClick, interactive, treatmentInfo }) => {
  const type = getToothType(num);

  let w = 28, h = 34;
  if (type === 'molar') { w = 32; h = 36; }
  else if (type === 'premolar') { w = 26; h = 32; }
  else if (type === 'canine') { w = 24; h = 36; }
  else { w = 22; h = 30; }

  let fill = '#f8f9fa';
  let stroke = '#94a3b8';
  if (isSelected) { fill = '#0F4C5C'; stroke = '#0F4C5C'; }
  else if (isMultiTreated) { fill = '#22C55E'; stroke = '#16A34A'; }
  else if (isTreated) { fill = '#E07A5F'; stroke = '#c0583f'; }

  const rootH = isUpper ? -12 : 12;

  return (
    <g
      onClick={interactive ? () => onClick(num) : undefined}
      style={{ cursor: interactive ? 'pointer' : 'default' }}
      data-testid={`tooth-${num}`}
    >
      <title>{`${num} - ${TOOTH_NAMES[num]}${treatmentInfo ? '\n' + treatmentInfo : ''}`}</title>
      <line x1={x} y1={y + (isUpper ? -h/2 : h/2)} x2={x} y2={y + (isUpper ? -h/2 : h/2) + rootH}
        stroke={stroke} strokeWidth="2" opacity="0.5" />
      <rect
        x={x - w/2} y={y - h/2} width={w} height={h} rx={6} ry={6}
        fill={fill} stroke={stroke} strokeWidth="1.5"
        className="transition-all duration-200"
      />
      <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle"
        fontSize="10" fontWeight="600" fill={isSelected || isTreated ? '#fff' : '#374151'}>
        {num}
      </text>
    </g>
  );
};

const DentalChart = ({ selectedTeeth = [], onToggleTooth, interactive = false, treatedTeethMap = {}, multiTreatTeeth = [] }) => {
  const spacing = 40;
  const startX = 30;
  const chartWidth = startX * 2 + spacing * 15;

  return (
    <div className="w-full overflow-x-auto" data-testid="dental-chart">
      <svg viewBox={`0 0 ${chartWidth} 200`} className="w-full max-w-[700px] mx-auto" style={{ minWidth: '500px' }}>
        <text x={chartWidth / 2} y={16} textAnchor="middle" fontSize="11" fill="#6B7280" fontWeight="600">Upper</text>
        <text x={chartWidth / 2} y={192} textAnchor="middle" fontSize="11" fill="#6B7280" fontWeight="600">Lower</text>
        <line x1={chartWidth / 2} y1={95} x2={chartWidth / 2} y2={105} stroke="#cbd5e1" strokeWidth="1" />
        <line x1={20} y1={100} x2={chartWidth - 20} y2={100} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4" />
        <text x={10} y={60} textAnchor="middle" fontSize="9" fill="#94a3b8">R</text>
        <text x={chartWidth - 10} y={60} textAnchor="middle" fontSize="9" fill="#94a3b8">L</text>
        <text x={10} y={150} textAnchor="middle" fontSize="9" fill="#94a3b8">R</text>
        <text x={chartWidth - 10} y={150} textAnchor="middle" fontSize="9" fill="#94a3b8">L</text>

        {UPPER_TEETH.map((num, i) => (
          <ToothShape
            key={num} num={num}
            x={startX + i * spacing} y={55}
            isUpper={true}
            isSelected={selectedTeeth.includes(num)}
            isTreated={!!treatedTeethMap[num] && !selectedTeeth.includes(num)}
            isMultiTreated={multiTreatTeeth.includes(num) && !selectedTeeth.includes(num)}
            onClick={onToggleTooth}
            interactive={interactive}
            treatmentInfo={treatedTeethMap[num]}
          />
        ))}
        {LOWER_TEETH.map((num, i) => (
          <ToothShape
            key={num} num={num}
            x={startX + i * spacing} y={145}
            isUpper={false}
            isSelected={selectedTeeth.includes(num)}
            isTreated={!!treatedTeethMap[num] && !selectedTeeth.includes(num)}
            isMultiTreated={multiTreatTeeth.includes(num) && !selectedTeeth.includes(num)}
            onClick={onToggleTooth}
            interactive={interactive}
            treatmentInfo={treatedTeethMap[num]}
          />
        ))}
      </svg>
      {interactive && selectedTeeth.length > 0 && (
        <div className="mt-2 text-sm text-[#6B7280] text-center">
          Selected: {selectedTeeth.sort((a,b) => a-b).join(', ')}
        </div>
      )}
    </div>
  );
};

export { TOOTH_NAMES };
export default DentalChart;
